import { useCallback, useMemo, useEffect } from 'react';
import { useMutation } from 'data-fetching-client';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import NavMarker from 'react-rhumb/NavMarker';
import unescapedText from 'I18n/utils/unescapedText';
import { parse as parseParams } from 'hub-http/helpers/params';
import FormattedMessage from 'I18n/components/FormattedMessage';
import Loadable from 'UIComponents/decorators/Loadable';
import { getDataFetchingClientRequestState } from 'marketplace-ui-common/dataFetchingClient/utils/getDataFetchingClientRequestState';
import * as RequestState from 'marketplace-ui-common/requestStates/RequestState';
import { PENDING } from 'marketplace-ui-reviews/constants/UnreviewableReason';
import Review from 'marketplace-ui-reviews/records/Review';
import getDisplayedReviewerName from 'marketplace-ui-reviews/utils/getDisplayedReviewerName';
import { SUBMIT_REVIEW } from 'marketplace-ui-reviews/queries/submitReviewQuery';
// @ts-expect-error untyped dependency
import { postNotification } from 'ReduxMessenger/actions/NotificationActions';
import { getUserEmail, getUserFirstName, getUserLastName
// @ts-expect-error untyped dependency
} from '../../selectors/auth';
import { jsx as _jsx } from "react/jsx-runtime";
const AsyncReviewWizard = Loadable({
  loader: () => import( /* webpackChunkName: 'review-wizard' */'marketplace-ui-reviews/components/wizard/ThemeReviewWizard')
});
export default function ReviewWizard({
  name,
  offeringId,
  offeringType,
  onClose,
  onSuccess,
  onTrackInteraction,
  providerName,
  slug,
  supportLink,
  thumbnailUrl,
  versionNumber,
  reviewType,
  unreviewableReason
}) {
  const dispatch = useDispatch();
  useEffect(() => {
    if (unreviewableReason && unreviewableReason !== `ReviewError.${PENDING}`) {
      dispatch(postNotification({
        id: 'ASSET_UNREVIEWABLE_NOTIFICATION',
        type: 'danger',
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-assets-ui.themes.details.reviews.unreviewable.titleText"
        }),
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `marketplace-ui-reviews.error.${unreviewableReason}`,
          options: {
            offeringType: unescapedText(`marketplace-assets-ui.details.offeringType.${offeringType}_lowercase`)
          }
        })
      }));
    }
  }, [offeringType, unreviewableReason, dispatch]);
  const userEmail = useSelector(getUserEmail);
  const firstName = useSelector(getUserFirstName);
  const lastName = useSelector(getUserLastName);
  const displayName = getDisplayedReviewerName({
    firstName,
    lastName
  });
  const [submitReview, {
    loading: reviewWriteLoading,
    error: reviewWriteError
  }] = useMutation(SUBMIT_REVIEW);
  const reviewWriteStatus = getDataFetchingClientRequestState(reviewWriteError, reviewWriteLoading);
  const {
    search
  } = useLocation();
  const {
    eco_review_source,
    rating
  } = parseParams(search.slice(1));
  const reviewSource = eco_review_source || 'details';
  const overallRating = /^[1-5]$/.test(rating) ? parseInt(rating, 10) : undefined;
  const defaultReview = useMemo(() => {
    let storedReview = null;
    try {
      storedReview = JSON.parse(sessionStorage.getItem(`${slug}_review`));
    } catch (__error) {
      // pass through
    }
    let displayedReview = storedReview ? new Review(storedReview) : new Review();
    if (overallRating) {
      displayedReview = displayedReview.setOverallRating(overallRating);
    }
    return displayedReview;
  }, [overallRating, slug]);
  const handleSubmit = useCallback(review =>
  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  new Promise((resolve, reject) => {
    return submitReview({
      variables: {
        review: review.set('createdAt', new Date().valueOf()).set('entityId', offeringId).set('reviewType', reviewType)
      }
    }).then(() => {
      resolve();
      onSuccess();
    }).catch(err => {
      reject(err);
    });
  }), [submitReview, offeringId, reviewType, onSuccess]);
  const updateSessionStorage = useCallback(updatedReview => {
    sessionStorage.setItem(`${slug}_review`, JSON.stringify(updatedReview));
  }, [slug]);
  const onWizardLoad = useCallback(() => {
    onTrackInteraction('open review wizard', {
      reviewSource
    });
  }, [onTrackInteraction, reviewSource]);
  const onStepChange = useCallback((prevStep, nextStep) => {
    onTrackInteraction('change review wizard step', {
      value: nextStep,
      prevStep,
      reviewSource
    });
  }, [onTrackInteraction, reviewSource]);
  const onReject = useCallback(() => {
    onTrackInteraction('dismiss review wizard', {
      reviewSource
    });
    onClose();
  }, [onTrackInteraction, onClose, reviewSource]);
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "REVIEW_WIZARD_LOAD",
    children: !unreviewableReason && /*#__PURE__*/_jsx(AsyncReviewWizard, {
      reviewableName: name,
      reviewableIconUrl: thumbnailUrl,
      reviewableProvider: providerName,
      failed: reviewWriteStatus === RequestState.FAILED,
      defaultReview: defaultReview,
      displayName: displayName,
      loading: reviewWriteStatus === RequestState.PENDING,
      onReject: onReject,
      onChange: updateSessionStorage,
      onConfirm: handleSubmit,
      reviewType: reviewType,
      onStepChange: onStepChange,
      onWizardLoad: onWizardLoad,
      supportLink: supportLink,
      userEmail: userEmail,
      versionNumber: versionNumber
    })
  });
}